/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { ChangeDetectionStrategy, Component, computed, forwardRef, input, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { dateToIsoDate, isoDateToDate, provideBuddhistDateAdapter } from '../../utils/buddhist-date-adapter';

/**
 * A date field that shows and accepts **Buddhist Era** years — `20/05/2567` —
 * with a Thai calendar, in place of `<input type="date">`, which renders in the
 * browser's locale and cannot show a Buddhist year at all.
 *
 * It is a drop-in for the native input: the model is the same `yyyy-MM-dd`
 * Common Era string, or '' when empty, so code behind the field is unchanged.
 * The model updates when an entry is committed — on blur, Enter, or a pick from
 * the calendar — as the native input's `change` event did, not on every key.
 * Text that is not a date, or a date outside `min`/`max`, commits as '' and
 * marks the field invalid, so a required check on the model catches it.
 *
 * ```html
 * <aca-date-input class="aca-upload__input" inputId="dms-date" [(ngModel)]="form.documentDate" />
 * ```
 *
 * The host is the visible box: give it the class the native input had. The
 * text field inside is borderless and fills it.
 */
@Component({
  selector: 'aca-date-input',
  standalone: true,
  imports: [ReactiveFormsModule, MatDatepickerModule, MatIconModule, TranslatePipe],
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'aca-date-input',
    '[class.aca-date-input--invalid]': 'invalid()',
    '[class.aca-date-input--disabled]': 'control.disabled'
  },
  providers: [...provideBuddhistDateAdapter(), { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DateInputComponent), multi: true }]
})
export class DateInputComponent implements ControlValueAccessor {
  /** `id` of the text field, for a `<label for>` outside the component. */
  inputId = input<string | null>(null);
  ariaLabel = input<string | null>(null);
  placeholder = input<string | null>(null);
  /** Earliest and latest allowed dates, as `yyyy-MM-dd` like the model. */
  min = input<string | null>(null);
  max = input<string | null>(null);

  readonly minDate = computed(() => isoDateToDate(this.min()));
  readonly maxDate = computed(() => isoDateToDate(this.max()));

  readonly control = new FormControl<Date | null>(null);

  private onChange: (value: string) => void = () => {};
  private onTouchedFn: () => void = () => {};

  /** What is in the field is not a date the model can take; the model holds '' meanwhile. */
  invalid(): boolean {
    const errors = this.control.errors;
    return !!(errors?.['matDatepickerParse'] || errors?.['matDatepickerMin'] || errors?.['matDatepickerMax']);
  }

  writeValue(value: string | null | undefined): void {
    this.control.setValue(isoDateToDate(value), { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(disabled: boolean): void {
    if (disabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  onDateChange(event: MatDatepickerInputEvent<Date>): void {
    this.onChange(this.invalid() ? '' : dateToIsoDate(event.value));
  }

  onTouched(): void {
    this.onTouchedFn();
  }
}
